import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useChangeVisibilityMutation, useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GroupFilterSettingsTree from './GroupFilterSettingsTree';
import useNotification from '../../../../utility/hooks/useNotification';
import AdmicityBackdrop from '../../../../shared-components/AdmicityBackdrop';
import PropTypes from 'prop-types';

const rootNodesWithVisibilityChangeAllowed = ['EMPLOYEES', 'ENQUIRY', 'FUTURE-ADMISSION'];

const GroupFilters = ({schoolInfo}) => {
    const {
        data: groupFilters,
        refetch: refetchGroupFilters,
        isFetching: isRefetchingGroupFilters,
        isLoading: isLoadingGroupFilters
    } = useGetGroupFiltersQuery({
        fetchOnlyVisible: false,
        maxLevel: 2
    });
    const [changeVisibility, {
        isSuccess: isChangeVisibilitySuccess,
        isError: isChangeVisibilityError,
        isLoading: isChangingVisibilityInProgress
    }] = useChangeVisibilityMutation();

    const [modifiedFilters, setModifiedFilters] = useState({});
    const {
        showErrorNotification,
        showSuccessNotification
    } = useNotification();

    useEffect(() => {
        if (isChangeVisibilityError) {
            showErrorNotification('Failed to update group filter visibility settings');
        } else if (isChangeVisibilitySuccess) {
            refetchGroupFilters();
            showSuccessNotification('Group filter visibility settings have been updated successfully');
        }
    }, [isChangeVisibilitySuccess, isChangeVisibilityError, refetchGroupFilters]);

    useEffect(() => {
        if (isRefetchingGroupFilters) {
            setModifiedFilters({});
        }
    }, [isRefetchingGroupFilters]);

    const handleTreeItemVisibilityChange = useCallback((node, makeVisible) =>
        setModifiedFilters(prev => {
            if (Object.prototype.hasOwnProperty.call(prev, node.nodeId) && node.isVisible === makeVisible) {
                const {
                    [node.nodeId]: _,
                    ...rest
                } = prev;

                return rest;
            }

            return {
                ...prev,
                [node.nodeId]: makeVisible
            };
        }), []);

    const filteredModules = useMemo(() => {
        return groupFilters?.modules?.filter(
            (module) => module.name === 'PRE-ADMISSION' || module.name === 'ON-ROLL' || module.name === 'FUTURE-ADMISSION' || module.name === 'EMPLOYEES' || (!schoolInfo.disableEnquiriesSync && module.name === 'ENQUIRY')
        ) || [];
    }, [groupFilters]);

    return (
        <Box
            sx={{
                mt: 2,
                width: {
                    xs: '100%',
                },
                maxWidth: 1200,
                mx: 'auto',
                position: 'relative',
            }}
        >
            {!isLoadingGroupFilters && filteredModules.length > 0
                ? (
                    <>
                        <GroupFilterSettingsTree
                            data={filteredModules}
                            rootNodesWithVisibilityChangeAllowed={rootNodesWithVisibilityChangeAllowed}
                            onVisibilityChange={handleTreeItemVisibilityChange}
                        />
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}>
                            <Button
                                sx={{
                                    mt: 2,
                                    backgroundColor: '#186BBE',
                                    boxShadow: 'none',
                                    width: {
                                        xs: '100%',
                                        sm: 'auto'
                                    },
                                    '&:hover': {
                                        backgroundColor: '#155FA7',
                                        boxShadow: 'none'
                                    }
                                }}
                                variant="contained"
                                disabled={isChangingVisibilityInProgress || Object.keys(modifiedFilters).length === 0}
                                onClick={async () =>
                                    await changeVisibility({
                                        groupFilters: Object.entries(modifiedFilters).map(([id, isVisible]) => ({
                                            id,
                                            isVisible
                                        }))
                                    })
                                }
                            >
                                Save
                            </Button>
                        </Box>
                    </>
                )
                : (
                    <Box paddingTop={2}>
                        <Typography variant="h5" gutterBottom>
                            No Group Filters Available
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            {'Group filters may not be available because they haven\'t been synchronized yet, or an error occurred while retrieving them. Please try again later.'}
                        </Typography>
                    </Box>
                )}
            <AdmicityBackdrop
                open={isLoadingGroupFilters || isRefetchingGroupFilters || isChangingVisibilityInProgress}
            />
        </Box>
    );
};

GroupFilters.propTypes = {
    schoolInfo: PropTypes.object
};

export default GroupFilters;
